import { Flex } from '@chakra-ui/react';
import React, { useState } from 'react';
import { FieldHeader } from './common/FieldHeader';
import { RadioDiffModal } from './Radio/RadioDiffModal';
import { RadioDisplay } from './Radio/RadioDisplay';
import { RadioEmptyState } from './Radio/RadioEmptyState';
import RadioUpdateModal from './Radio/RadioUpdateModal';
import {
  FormFieldDataAvailability,
  FormFieldRadio
} from '@publishing-engine/domain/types/article';
import { FieldType } from '@publishing-engine/domain/types';
import { IAvailableWorkflowActions } from '@publishing-engine/domain/models';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';

/**
 * PublishingModel is ALMOST an Options-Field, but not quite.
 * Its value is { model: x|y, acknowledgeCharge: boolean }.
 * For editing and display purposes, we only care about the 'model' value.
 * So we treat this file as a Façade, pluck out the 'model' value,
 * and defer the rest to Generic Radio Field.
 */

type IProps = {
  formFieldDefinition: FormFieldDataAvailability;
  value: FieldType<FormFieldDataAvailability>;
  previousValue?: FieldType<FormFieldDataAvailability>;
  availableActions: IAvailableWorkflowActions;
  onChange?: (value: { status: string}) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?:boolean;
};

export const DataAvailabilityField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const isEditable = !!onChange;

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const statusValue = value?.status;
  const previousStatusValue = previousValue?.status;

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  const asRadio: FormFieldRadio = {
    ...formFieldDefinition,
    component: 'Custom:RadioGroup',
    params: {
      ...formFieldDefinition.params,
      validations: {
        ...formFieldDefinition.params.validations,
        kind: 'options',
        options: Object.keys(formFieldDefinition.params.labels.options)
      }
    }
  };

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && onChange && (
        <RadioUpdateModal
          formField={asRadio}
          isOpen={isEditing}
          onCancel={() => {
            setIsEditing(false);
          }}
          value={statusValue!}
          onSubmit={(value: string) => {
            setIsEditing(false);
            onChange({
              status: value,
            });
          }}
        />
      )}

      {isUpdated && showDiffModal ? (
        <RadioDiffModal
          fieldName={formFieldDefinition.params.labels.main}
          value={formFieldDefinition.params.labels.options[statusValue!]?.main!}
          previousValue={formFieldDefinition.params.labels.options[previousStatusValue!]?.main!}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {value ? (
        <RadioDisplay
          value={formFieldDefinition.params.labels.options[statusValue!]?.main!}
        />
      ) : (
        <RadioEmptyState />
      )}
    </Flex>
  );
};

